import { useCallback, useMemo } from 'react';
import { generatePath, matchPath } from 'react-router-dom';
import {
  buildQueryFilters,
  PermissionAction,
  PermissionResource,
  useGetChildOrganizations,
  useGetOrganization,
} from '@sib/api';
import { DEFAULT_PAGE, MAXIMUM_PAGINATION } from '@sib/api/constants';
import { useCan, useUserSetupStore } from '@sib/auth';
import { paths } from '@spendbrain/config/paths';
import { usePowerBiReports } from './use-power-bi-reports';

export const useSpendbrainRoutesAccess = () => {
  const { powerBiReports } = usePowerBiReports();
  const showReportsDirectory = powerBiReports.length > 0;
  const canAccess = useCan();
  const {
    organization: { id: organizationId },
    isSibEmployee,
  } = useUserSetupStore();

  const ticketsPath = useMemo(
    () =>
      isSibEmployee
        ? generatePath(paths.tickets.employee.root, { orgId: organizationId })
        : paths.tickets.client.root,
    [isSibEmployee, organizationId]
  );
  const { data: organizationData } = useGetOrganization(organizationId);
  const hasVectorClientId = !!organizationData?.vectorClientId;
  const hasNetsuiteCustomerId = !!organizationData?.netsuiteCustomerId;
  const hasBillingAccess = hasVectorClientId || hasNetsuiteCustomerId;

  const { data: childOrgsData } = useGetChildOrganizations(
    organizationId || '',
    {
      page: DEFAULT_PAGE,
      size: MAXIMUM_PAGINATION,
      filter: buildQueryFilters([
        { field: 'micrositesEnabled', operator: 'eq', value: true },
      ]),
    }
  );

  const hasChildren = (childOrgsData?.totalItems ?? 0) > 0;

  const availableRoutes = useMemo(
    () =>
      [
        ...(hasChildren
          ? [
              {
                href: paths.subsidiaryOrganization,
              },
            ]
          : []),
        ...(canAccess({
          permissions: [
            {
              actions: PermissionAction.READ,
              resources: PermissionResource.SpendAreas,
            },
            {
              actions: PermissionAction.READ,
              resources: PermissionResource.ActionItems,
            },
            {
              actions: PermissionAction.CREATE,
              resources: PermissionResource.SpendAreas,
            },
          ],
          operator: 'OR',
        })
          ? [
              {
                href: paths.dashboard,
                access: {
                  permissions: {
                    actions: PermissionAction.READ,
                    resources: PermissionResource.Dashboard,
                  },
                },
              },
            ]
          : []),
        {
          href: paths.savings,
          access: {
            permissions: {
              actions: PermissionAction.READ,
              resources: PermissionResource.Savings,
            },
          },
        },
        {
          href: paths.services,
          access: {
            permissions: [
              {
                actions: PermissionAction.READ,
                resources: PermissionResource.SpendAreas,
              },
              {
                actions: PermissionAction.READ,
                resources: PermissionResource.ActionItems,
              },
              {
                actions: PermissionAction.CREATE,
                resources: PermissionResource.SpendAreas,
              },
            ],
            operator: 'OR',
          },
        },
        {
          href: paths.documents.root,
          access: {
            permissions: {
              actions: PermissionAction.READ,
              resources: PermissionResource.Documents,
            },
          },
        },
        {
          href: paths.locations,
          access: {
            permissions: {
              actions: PermissionAction.READ,
              resources: PermissionResource.Locations,
            },
          },
        },

        ...(showReportsDirectory ? [{ href: paths.reports }] : []),
        {
          href: ticketsPath,
          access: {
            permissions: [
              {
                actions: PermissionAction.READ,
                resources: PermissionResource.Tickets,
              },
              {
                actions: PermissionAction.CREATE,
                resources: PermissionResource.Tickets,
              },
            ],
            operator: 'OR',
          },
        },
        {
          href: paths.upcomingEvents,
        },
        {
          href: paths.chat,
        },
        {
          href: paths.yourTeam,
          access: {
            permissions: {
              actions: PermissionAction.READ,
              resources: PermissionResource.Teams,
            },
          },
        },
        {
          href: paths.account,
        },
        ...(hasBillingAccess
          ? [
              {
                href: paths.billing,
              },
            ]
          : []),
        {
          href: paths.vendors,
          access: {
            permissions: {
              actions: PermissionAction.READ,
              resources: PermissionResource.Vendors,
            },
          },
        },
        {
          href: paths.billPay,
        },
        {
          href: paths.contractTerms,
        },
        {
          href: paths.help,
        },
        {
          href: 'https://aboutsib.com/',
        },
      ].filter(
        ({ access }) =>
          !access || canAccess(access as Parameters<typeof canAccess>[0])
      ),
    [
      canAccess,
      hasBillingAccess,
      hasChildren,
      showReportsDirectory,
      ticketsPath,
    ]
  );

  const isRouteAvailable = useCallback(
    (path: string) =>
      availableRoutes.some(route => route.href === path) ||
      availableRoutes.some(route => matchPath({ path }, route.href)),
    [availableRoutes]
  );

  return { availableRoutes, isRouteAvailable };
};
